import {Component, ElementRef, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {UserService} from "../../../services/user.service";
import {PopupService} from "../../../services/popup.service";
import {Router} from "@angular/router";
import {AuthService} from "../../../services/auth.service";
import {updateUser} from "../../../state/auth/auth.actions";
import {select, Store} from "@ngrx/store";
import {AppState} from "../../../state/app.state";
import {SerpApiService} from "../../../services/serp-api.service";
import {switchMap} from "rxjs/operators";
import {debounceTime, distinctUntilChanged, filter, Observable, of, Subject, Subscription, take} from "rxjs";
import {selectDomains, selectLanguages, selectLoading} from "../../../state/serpApi/serpApi.selectors";
import * as SerpApiActions from '../../../state/serpApi/serpApi.actions';
import {selectError} from "../../../state/auth/auth.selectors";
import {HeaderService} from "../../../services/header.service";
import {FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors} from '@angular/forms';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss']
})
export class ProfileComponent implements OnInit, OnDestroy {
  user: any;
  selectedFile: File = null;

  name: string = "";
  email: string = "";

  currentPassword: string = "";
  newPassword: string = "";
  newPasswordConfirmation: string = "";

  profileForm: FormGroup;
  profileSubmitted = false;

  passwordForm: FormGroup;
  passwordSubmitted = false;

  searchLocation: string = '';
  filteredLocations: any[] = [];
  selectedLocation: any = null;
  dropdownOpen = false;
  private searchTerms = new Subject<string>();
  private clickListener: () => void;

  filteredDomains$: Observable<any[]>;
  selectedDomain: any = null;
  domainDropdownOpen = false;

  filteredLanguages$: Observable<any[]>;
  selectedLanguage: any = null;
  languageDropdownOpen: boolean = false;

  private subscriptions = new Subscription();

  loading$: Observable<boolean>;
  error$: Observable<any>;

  constructor(
    private userService: UserService,
    private authService: AuthService,
    private popupService: PopupService,
    private store: Store<AppState>,
    public router: Router,
    private serpApiService: SerpApiService,
    private elRef: ElementRef,
    private renderer: Renderer2,
    private headerService: HeaderService,
    private fb: FormBuilder
  ) {

    // this.user = Object.assign({}, this.userService.user);
    // this.user.current_password = this.user.new_password  = this.user.new_password_confirm = "";

    // Initialize forms early to avoid template errors
    this.initializeProfileForm();
    this.initializePasswordForm();

    this.filteredLanguages$ = this.store.pipe(select(selectLanguages));
    this.filteredDomains$ = this.store.pipe(select(selectDomains));

    this.authService.authUser().subscribe(user => {
      this.user = user;

      if (user) {
        this.name = user.name;
        this.email = user.email;

        // Set SerpApi
        this.selectedLocation = user.serpapi_location
        this.searchLocation = user.serpapi_location
        this.selectedDomain = user.serpapi_domain
        this.selectedLanguage = user.serpapi_language

        // Update profile form with user data
        this.profileForm.patchValue({ name: user.name });
      }
    });

    this.loading$ = this.store.pipe(select(selectLoading));
    this.error$ = this.store.pipe(select(selectError));

  }

  ngOnInit(): void {

    this.headerService.setHeaderState('Update Profile', null, true);

    this.searchTerms.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => {
        if (term.trim() === '') {
          this.dropdownOpen = false;
          return of([]);
        }
        return this.serpApiService.getLocations(term);
      })
    ).subscribe(
      locations => {
        this.filteredLocations = locations;
        this.dropdownOpen = locations.length > 0;
      },
      error => {
        console.error('Error fetching locations:', error);
        this.dropdownOpen = false;
      }
    );

    // Add click event listener to close dropdown on outside click
    this.clickListener = this.renderer.listen('document', 'click', (event: Event) => this.onClickOutside(event));

    this.store.dispatch(SerpApiActions.loadLanguages());
    this.store.dispatch(SerpApiActions.loadDomains());

  }

  ngOnDestroy(): void {
    // Remove click event listener when the component is destroyed
    if (this.clickListener) {
      this.clickListener();
    }

    // Unsubscribe from all subscriptions
    this.subscriptions.unsubscribe();
  }

  updateProfile() {
    this.profileSubmitted = true;

    // Stop if form is invalid
    if (this.profileForm.invalid) {
      return;
    }

    const formValues = this.profileForm.value;
    const trimmedName = formValues.name.trim();

    this.userService.updateProfile({ id: this.user.id, name: trimmedName })
      .then(response => {
        if(response.data.status) {

          const updatedUser = {...this.user, name: trimmedName};
          this.store.dispatch(updateUser({ user: updatedUser }));

          this.popupService.notify('Profile has been successfully updated!', 'Update success', 'success');
          this.profileSubmitted = false;
        } else {
          this.popupService.error("Profile update failed", response.data.message, "OK");
        }
      })
      .catch(error => {
        this.popupService.error("Error occured", error.data.message, "OK");
      });

  }

  updatePassword() {
    this.passwordSubmitted = true;

    // Stop if form is invalid
    if (this.passwordForm.invalid) {
      return;
    }

    const formValues = this.passwordForm.value;

    this.userService.changePassword({
      id: this.user.id,
      current_password: formValues.currentPassword,
      new_password: formValues.newPassword,
      new_password_confirmation: formValues.newPasswordConfirmation
    })
      .then(response => {
        if(response.data.status){
          this.popupService.notify('Password has been successfully updated!', 'Update success', 'success');
          // Reset form after successful update
          this.passwordForm.reset();
          this.passwordSubmitted = false;
        } else {
          this.popupService.notify(response.data.message, 'Password update failed', 'error');
        }
      })
      .catch(error => {
        this.popupService.notify(error.message ?? error.data.message, 'Error occurred', 'error');
      });
  }

  onFileSelected(event: Event) {
    const element = event.currentTarget as HTMLInputElement;
    let file = element.files ? element.files[0] : null;

    if (file) {
      let formData = new FormData();
      formData.append('file', file);

      this.userService.updateAvatar(formData).then(response => {
        if (response.data.status) {
          // this.user.profile_photo = response.data.profile_photo;

          const updatedUser = {...this.user, profile_photo: response.data.profile_photo};
          this.store.dispatch(updateUser({ user: updatedUser }));

          this.popupService.success('', 'Profile photo updated successfully', 'OK');
        } else {
          this.popupService.error('', 'Cannot upload the selected file. Please try again', 'OK');
        }
      }).catch(error => {
        this.popupService.error('Something went wrong', error, 'OK');
      });
    } else {
      this.popupService.error('', 'No file was selected, therefore your profile picture will not be updated', 'OK');
    }
  }

  updateSerpApi(): void {
    console.log('updateSerpApi')

    if (this.user && this.selectedLocation && this.selectedDomain && this.selectedLanguage) {
      this.store.dispatch(SerpApiActions.updateSerpApi({
        user_id: this.user.id,
        location: this.selectedLocation,
        domain: this.selectedDomain,
        language: this.selectedLanguage
      }));

      this.handleSerpApiUpdate();
    }
  }

  handleSerpApiUpdate(): void {
    const loadingSub = this.loading$.pipe(
      filter(loading => !loading), // Wait until loading is false
      take(1)
    ).subscribe(() => {
      this.store.pipe(
        select(selectError),
        take(1)
      ).subscribe(error => {
        if (error) {
          console.error('Update SerpApi Failure:', error);
          this.popupService.notify(error.error.message, 'Research Area Update Failed', 'error');
        } else {
          console.log('Update SerpApi Success');
          this.popupService.notify('Research Area has been successfully updated!', 'Research Area Update Successful', 'success');
          // this.router.navigate(['/settings/account']);

          // Update Profile
          const updatedUser = {...this.user, serpapi_location: this.selectedLocation, serpapi_domain: this.selectedDomain, serpapi_language: this.selectedLanguage };
          this.store.dispatch(updateUser({ user: updatedUser }));

        }
      });
    });

    this.subscriptions.add(loadingSub);
  }

  onLocationSearchChange(search: string): void {
    this.searchTerms.next(search);
  }

  onInputFocus(): void {
    if (this.searchLocation.trim() !== '') {
      this.searchTerms.next(this.searchLocation);
    }
  }

  selectLocation(location: any) {
    console.log('selectLocation', location)
    this.selectedLocation = location.canonical_name;
    this.searchLocation = location.canonical_name;
    this.dropdownOpen = false;
  }

  onClickOutside(event: Event): void {
    if (this.elRef.nativeElement.contains(event.target)) {
      return;
    }
    this.dropdownOpen = false;
  }

  toggleDomainDropdown() {
    this.domainDropdownOpen = !this.domainDropdownOpen;
  }

  toggleLanguageDropdown() {
    this.languageDropdownOpen = !this.languageDropdownOpen;
  }

  selectDomain(domain: any) {
    // console.log('selectDomain', domain)
    this.selectedDomain = domain.domain;
    this.domainDropdownOpen = false;
  }

  selectLanguage(language: any) {
    // console.log('selectLanguage', language)
    this.selectedLanguage = language.language_code;
    this.languageDropdownOpen = false;
  }

  onImageError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = 'assets/images/human.svg'; // Fallback image when error occurs
  }

  // Initialize profile form with validators
  private initializeProfileForm(): void {
    this.profileForm = this.fb.group({
      name: [this.name, [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(100),
        this.noWhitespaceValidator.bind(this)
      ]]
    });

    // Update form value when user data changes
    if (this.user && this.user.name) {
      this.profileForm.patchValue({ name: this.user.name });
    }
  }

  // Initialize password form with validators
  private initializePasswordForm(): void {
    this.passwordForm = this.fb.group({
      currentPassword: ['', [Validators.required]],
      newPassword: ['', [
        Validators.required,
        Validators.minLength(8),
        this.passwordStrengthValidator()
      ]],
      newPasswordConfirmation: ['', [Validators.required]]
    }, {
      validators: this.passwordMatchValidator()
    });
  }

  // Custom validator for password strength
  private passwordStrengthValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }

      const hasUpperCase = /[A-Z]/.test(value);
      const hasLowerCase = /[a-z]/.test(value);
      const hasNumeric = /[0-9]/.test(value);

      const passwordValid = hasUpperCase && hasLowerCase && hasNumeric;

      return !passwordValid ? {
        passwordStrength: {
          hasUpperCase,
          hasLowerCase,
          hasNumeric
        }
      } : null;
    };
  }

  // Custom validator to check if passwords match
  private passwordMatchValidator() {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const newPassword = formGroup.get('newPassword')?.value;
      const confirmation = formGroup.get('newPasswordConfirmation')?.value;

      if (!newPassword || !confirmation) {
        return null;
      }

      return newPassword === confirmation ? null : { passwordMismatch: true };
    };
  }

  // Custom validator to prevent whitespace-only values
  private noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const isWhitespace = (control.value || '').trim().length === 0;
    return isWhitespace ? { whitespace: true } : null;
  }

  // Getter methods for form controls
  get p() {
    return this.profileForm.controls;
  }

  get f() {
    return this.passwordForm.controls;
  }

  get currentPasswordControl() {
    return this.passwordForm.get('currentPassword');
  }

  get newPasswordControl() {
    return this.passwordForm.get('newPassword');
  }

  get newPasswordConfirmationControl() {
    return this.passwordForm.get('newPasswordConfirmation');
  }

  /*
  updateProfileAndPassword(){

    this.userService.updateProfile({ id: this.user.id, name: this.name })
      .then(response => {
        if(response.data.status) {

          const updatedUser = {...this.user, name: this.name};
          this.store.dispatch(updateUser({ user: updatedUser }));

          this.popupService.success("Profile update completed", response.data.message, "OK");
        } else {
          this.popupService.error("Profile update failed", response.data.message, "OK");
        }
      })
      .catch(error => {
        this.popupService.error("Error occured", error.data.message, "OK");
      });

    // console.log(this.user);
    if(this.currentPassword && this.newPassword && this.newPasswordConfirmation) {
      // User wants to reset password
      this.userService.changePassword({ id: this.user.id, current_password: this.currentPassword, new_password: this.newPassword, new_password_confirmation: this.newPasswordConfirmation  })
        .then(response => {
          if(response.data.status){
            this.popupService.success("Password update completed", response.data.message, "OK");
            this.userService.checkAuth();
          } else {
            this.popupService.error("Password update failed", response.data.message, "OK");
          }
        })
        .catch(error => {
          this.popupService.error("Error occured", error.message ?? error.data.message, "OK");
        });
    }
  }
  */

  /*
  onFileSelected(event) {
    this.selectedFile = event.target.files[0];

    if(this.selectedFile){
      let formData = new FormData();
      formData.append('file', this.selectedFile);

      this.userService.updateAvatar(formData).then(response => {
        if(response.data.status){
          this.user.profile_photo = response.data.profile_photo;
          this.popupService.success('', 'Profile photo was updated successfully', 'OK');
        } else {
          this.popupService.error('', 'Cannot upload the selected file. Please try again', 'OK');
        }
      }).catch(error => {
        this.popupService.error('Something went wrong', error, 'OK');
      });
    } else {
      this.popupService.error('', 'No file was selected, therefore your report picture will not be updated', 'OK');
    }
  }
  */

}
